import { Alert, AlertDescription, AlertTitle } from '@aitoken/ui/components/alert';
import { Button } from '@aitoken/ui/components/button';
import { CopyId } from '@aitoken/ui/components/copy-id';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@aitoken/ui/components/empty';
import { Skeleton } from '@aitoken/ui/components/skeleton';
import { Link, type ErrorComponentProps } from '@tanstack/react-router';
import { AlertCircle, Compass, Inbox, Lock, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { isApiError } from '@/lib/api';

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <Empty className={className}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon />
        </EmptyMedia>
        <EmptyTitle className="text-base font-semibold">{title}</EmptyTitle>
        {description ? <EmptyDescription>{description}</EmptyDescription> : null}
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

/** 查询失败：说明发生了什么，并给出重试与 request_id */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const message = error instanceof Error ? error.message : '加载失败';
  const requestId = isApiError(error) ? error.requestId : null;
  return (
    <Alert variant="destructive" className={className}>
      <AlertCircle />
      <AlertTitle>加载失败：{message}</AlertTitle>
      <AlertDescription>
        <div className="flex flex-wrap items-center gap-3">
          {requestId ? (
            <span className="inline-flex items-center gap-1 text-xs">
              request_id <CopyId value={requestId} />
            </span>
          ) : null}
          {onRetry ? (
            <Button size="xs" variant="outline" onClick={onRetry}>
              重试
            </Button>
          ) : null}
        </div>
      </AlertDescription>
    </Alert>
  );
}

export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="flex flex-col" aria-busy="true" aria-label="加载中">
      <div className="flex h-11 items-center gap-4 border-b border-rule px-5">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex h-11 items-center gap-4 border-b border-rule px-5 last:border-0">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className="h-3 flex-1" style={{ opacity: 1 - r * 0.08 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true" aria-label="加载中">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-48 rounded-lg" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-28 rounded-[20px] bg-card" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-[20px] bg-card" />
    </div>
  );
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4 p-8">
      <ErrorState error={error} onRetry={reset} />
    </div>
  );
}

export function NotFound() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <EmptyState
        icon={Compass}
        title="找不到这个页面"
        description="链接可能已失效，或对象已被删除。"
        action={
          <Button asChild size="sm" variant="outline">
            <Link to="/">回到经营总览</Link>
          </Button>
        }
      />
    </div>
  );
}

export function Forbidden() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <EmptyState
        icon={Lock}
        title="当前账号没有此页面的权限"
        description="如需访问，请联系系统管理员调整角色与权限点。"
      />
    </div>
  );
}
